import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader/PageHeader";
import ReasonModal from "../../components/ReasonModal/ReasonModal";
import { get, post } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import { approve, reject, user_icon } from "../../assets/img/imgIndex";
import "./UserDetails.css";
import "./UserVerification.css";

const isKitchenDoc = (doc) =>
  /kitchen/i.test(doc?.type_name || "") || /\/kitchen\//i.test(doc?.url || "");

const isIdentityDoc = (doc) =>
  /aadha|pan/i.test(doc?.type_name || "") || /id_proof/i.test(doc?.url || "");

const labelDocuments = (docs) => {
  const totals = {};
  docs.forEach((doc) => {
    const name = doc?.type_name || "Document";
    totals[name] = (totals[name] || 0) + 1;
  });

  const seen = {};
  return docs.map((doc) => {
    const name = doc?.type_name || "Document";
    seen[name] = (seen[name] || 0) + 1;
    const label = totals[name] > 1 ? `${name} ${seen[name]}` : name;
    return { ...doc, label };
  });
};

const isPdf = (url) => /\.pdf($|\?)/i.test(url || "");

const UserVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const id = location?.state?.id || null;
  const page = location?.state?.page || 1;
  const tab = location?.state?.tab || 2;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const goBack = () => {
    navigate("/users", { state: { page: page || 1, tab } });
  };

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const res = await get(`${URLS.USER_VERIFICATIONS}${id}/`);
        if (res?.status === "success") {
          setData(res?.data || null);
        }
      } catch (error) {
        console.error("Error fetching user verification:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  const documents = useMemo(() => {
    const list = Array.isArray(data?.documents) ? data.documents : [];
    const withUrl = list.filter((doc) => doc?.url);
    return {
      identity: labelDocuments(withUrl.filter((doc) => !isKitchenDoc(doc) && isIdentityDoc(doc))),
      kitchen: labelDocuments(withUrl.filter((doc) => isKitchenDoc(doc))),
      other: labelDocuments(
        withUrl.filter((doc) => !isKitchenDoc(doc) && !isIdentityDoc(doc)),
      ),
    };
  }, [data]);

  const userTypeLabel =
    typeof data?.user_type === "string" && data.user_type ? data.user_type : "";

  const openPreview = (file) => {
    if (!file?.url) return;
    setPreview({
      url: file.url,
      name: file.label || file.type_name || "Document",
      type: isPdf(file.url) ? "application/pdf" : "image/jpeg",
    });
  };

  const handleApprove = async () => {
    if (!id || actionLoading) return;
    setActionLoading(true);
    try {
      const res = await post(`${URLS.USER_VERIFICATIONS}${id}/approve/`, {});
      if (res?.status === "success") {
        toast.success(res?.message || "Approved successfully");
        goBack();
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleReasonSubmit = async (reason) => {
    if (!id || actionLoading) return;
    setActionLoading(true);
    try {
      const res = await post(`${URLS.USER_VERIFICATIONS}${id}/reject/`, {
        reason,
      });
      if (res?.status === "success") {
        toast.success(res?.message || "Rejected successfully");
        goBack();
      }
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="user-details-container user-verification-page">
      <PageHeader
        title="Verification"
        showBackButton={true}
        onBackClick={goBack}
      />

      {loading ? (
        <p className="verification-empty">Loading verification details...</p>
      ) : !data ? (
        <p className="verification-empty">Verification details not found.</p>
      ) : (
        <>
          <div className="profile-header-card">
            <div className="profile-image-wrapper">
              <img
                src={data?.profile_img_url || user_icon}
                alt="Profile"
              />
            </div>
            <div className="profile-info-main">
              <h2>{data?.name || "N/A"}</h2>
              <p>Status : {data?.verified_status_name || "N/A"}</p>
            </div>
            <div className="profile-divider"></div>
            <div className="profile-details-grid">
              <div className="detail-item">
                <span className="detail-label">Mobile No :</span>
                <span className="detail-value">
                  {data?.mob_no ? `+91-${data.mob_no}` : "N/A"}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Updated Date :</span>
                <span className="detail-value">
                  {data?.updated_at
                    ? moment(data.updated_at).format("DD-MM-YYYY")
                    : "N/A"}
                </span>
              </div>
              {userTypeLabel && (
                <div className="detail-item">
                  <span className="detail-label">User Type :</span>
                  <span className="detail-value">{userTypeLabel}</span>
                </div>
              )}
            </div>
          </div>

          <DocumentSection
            title="Aadhaar / PAN"
            documents={documents.identity}
            emptyText="No Aadhaar or PAN image uploaded"
            onPreview={openPreview}
          />
          <DocumentSection
            title="Kitchen"
            documents={documents.kitchen}
            emptyText="No kitchen image uploaded"
            onPreview={openPreview}
          />
          {documents.other.length > 0 && (
            <DocumentSection
              title="Other Documents"
              documents={documents.other}
              emptyText=""
              onPreview={openPreview}
            />
          )}

          {data?.rejected_reason ? (
            <div className="verification-reason">
              <label>Rejection Reason</label>
              <textarea value={data.rejected_reason} readOnly rows={4} />
            </div>
          ) : data?.verified_status === 1 ? (
            <div className="form-actions">
              <button
                type="button"
                className="btn-edit"
                onClick={() => setShowReasonModal(true)}
                disabled={actionLoading}
              >
                <img
                  src={reject}
                  width={12}
                  height={12}
                  style={{ marginRight: 4 }}
                  alt=""
                />
                Reject
              </button>
              <button
                type="button"
                className="btn-save"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                <img
                  src={approve}
                  width={18}
                  height={18}
                  style={{ marginRight: 4 }}
                  alt=""
                />
                Approve
              </button>
            </div>
          ) : null}
        </>
      )}

      {preview && (
        <div className="preview-overlay" onClick={() => setPreview(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="preview-close"
              onClick={() => setPreview(null)}
            >
              ×
            </button>
            {preview.type === "application/pdf" ? (
              <iframe
                src={`${preview.url}#toolbar=0`}
                title={preview.name}
                width="100%"
                height="70vh"
                style={{ border: "none", borderRadius: 8 }}
              />
            ) : (
              <img src={preview.url} alt={preview.name} className="preview-image" />
            )}
          </div>
        </div>
      )}

      <ReasonModal
        isOpen={showReasonModal}
        onClose={() => setShowReasonModal(false)}
        onSubmit={handleReasonSubmit}
      />
    </div>
  );
};

const DocumentSection = ({ title, documents, emptyText, onPreview }) => {
  return (
    <section className="verification-section">
      <h3>{title}</h3>
      {documents.length === 0 ? (
        <p className="verification-empty">{emptyText}</p>
      ) : (
        <div className="verification-image-grid">
          {documents.map((doc) => (
            <button
              type="button"
              key={doc.id || doc.url}
              className="verification-image-card"
              onClick={() => onPreview(doc)}
            >
              {isPdf(doc.url) ? (
                <div className="verification-pdf">PDF</div>
              ) : (
                <img src={doc.url} alt={doc.label} />
              )}
              <p>{doc.label}</p>
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default UserVerification;
